export type ShopifyComplianceTopic = 'customers/data_request' | 'customers/redact' | 'shop/redact';

export type ShopifyComplianceStatus = 'Received' | 'Completed' | 'Failed' | 'Retained';

export interface ShopifyComplianceRequest {
  id: string;
  topic: string;
  shopDomain: string;
  shopId: string | null;
  merchantId: string | null;
  status: string;
  receivedAt: string;
  completedAt: string | null;
  completedBy: string | null;
}

export interface ShopifyComplianceRequestDetail extends ShopifyComplianceRequest {
  customerShopifyId: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  orderIds: string[];
  payloadJson: string;
  notes: string | null;
}

export interface ShopifyComplianceOrderItemSnapshot {
  productId: string;
  productName: string | null;
  sku: string | null;
  quantity: number;
}

export interface ShopifyComplianceOrderSnapshot {
  salesOrderId: string;
  orderNumber: number;
  status: string;
  shopifyOrderId: string | null;
  customerFullName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  customerCompanyName: string | null;
  shippingAddressLine1: string | null;
  shippingCity: string | null;
  shippingState: string | null;
  shippingPostalCode: string | null;
  shippingCountry: string | null;
  billingAddressLine1: string | null;
  billingCity: string | null;
  billingState: string | null;
  billingPostalCode: string | null;
  billingCountry: string | null;
  items: ShopifyComplianceOrderItemSnapshot[];
}

export interface ShopifyComplianceCustomerSnapshot {
  customerId: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
}

export interface ShopifyComplianceReport {
  requestId: string;
  merchantId: string | null;
  orders: ShopifyComplianceOrderSnapshot[];
  customers: ShopifyComplianceCustomerSnapshot[];
}

export interface ShopifyComplianceRedactResult {
  requestId: string;
  status: string;
  ordersRedacted: number;
  customersRedacted: number;
  completedAt: string | null;
  completedBy: string | null;
}

export interface ShopifyComplianceRequestFilters {
  topic?: string;
  status?: string;
}

export interface ShopifyComplianceRedactPayload {
  retain?: boolean;
  notes?: string;
}

export interface ShopifyComplianceCompletePayload {
  notes?: string;
}

/** Shopify's own SLA is 30 days from receipt; ~20 days is the point support should act. */
export const SHOPIFY_COMPLIANCE_OVERDUE_DAYS = 20;

export function isShopifyComplianceRequestOverdue(request: ShopifyComplianceRequest, now: Date = new Date()): boolean {
  if (request.status !== 'Received') {
    return false;
  }
  const received = new Date(request.receivedAt);
  if (Number.isNaN(received.getTime())) {
    return false;
  }
  const ageMs = now.getTime() - received.getTime();
  return ageMs / (1000 * 60 * 60 * 24) >= SHOPIFY_COMPLIANCE_OVERDUE_DAYS;
}
